import { Link, useLocation } from "wouter";
import { MapPin, CheckCircle, Route, Heart, Bookmark, Package, Navigation, Star, Share2, Car } from "lucide-react";
import { CategoryIcon } from "./CategoryIcon";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { generateShopSlug } from "@/lib/slugify";
import { ShopRating } from "@/components/ShopRating";
import { ShopImage } from "@/components/ShopImage";
import { useAuth } from "@/lib/authContext";
import { useToast } from "@/hooks/use-toast";
import { useState } from "react";
import { PriceLevel } from "@/components/PriceLevel";
import { InsiderStar } from "@/components/InsiderStar";
import { ShareModal } from "@/components/ShareModal";
import { PostDetailModal } from "@/components/PostDetailModal";
import type { Shop } from "@shared/schema";
import { LoginModal } from "@/components/LoginModal";
import { UpgradeModal } from "@/components/UpgradeModal";
import { useShopInteractions } from "@/contexts/ShopInteractionsContext";
import { Clock as ClockIcon } from "lucide-react";
import { getOpeningStatus } from "@/lib/openingHours";
import { useRouteBuilder } from "@/contexts/RouteBuilderContext";
import { getShopNavigationUrl, openGoogleMaps } from "@/lib/googleMaps";
import { logAnalyticsEvent } from "@/lib/analytics";
import { useI18n } from "@/lib/i18n";
import { getShopProfileUrl } from "@/lib/shopUrl";
import { RatingModal } from "@/components/RatingModal";

interface ShopCardProps {
  shop: Shop;
  onAddToRoute?: () => void;
  showAddButton?: boolean;
  isSelected?: boolean;
  routeIndex?: number;
  openingStatusDay?: number;
}

export function ShopCard({ shop, onAddToRoute, showAddButton = true, isSelected = false, routeIndex, openingStatusDay }: ShopCardProps) {
  const { t, locale } = useI18n();
  // Safety check: skip rendering if shop data is essential but missing
  if (!shop || !shop.id) {
    console.warn("[ShopCard] Missing shop data, skipping render", shop);
    return null;
  }

  // VintMap Community rating - alleen gebruiken als er daadwerkelijk VintMap reviews zijn
  const communityRating = (shop.rating != null && !isNaN(shop.rating) && (shop.vintMapReviewCount ?? 0) > 0) ? shop.rating : null;
  const communityReviewCount = shop.vintMapReviewCount ?? 0;

  // Owner rating
  const ownerRating = shop.ownerRating != null && !isNaN(shop.ownerRating) ? shop.ownerRating : null;

  // Google rating - alleen gebruiken als het bestaat
  const googleRating = shop.google_rating != null && !isNaN(shop.google_rating) ? shop.google_rating : null;
  const googleReviewCount = shop.google_review_count;
  const googleReviewsUrl = shop.google_reviews_url;

  // Route count - veilig afhandelen
  const routeCount = shop.routeCount || 0;

  const { isLoggedIn, tier, isInsider } = useAuth();
  const { toast } = useToast();

  const [, navigate] = useLocation();

  // Route builder context
  const { toggleShop, addShop, shops: currentRouteShops, clearRoute, hasShop, isActive: routeActive } = useRouteBuilder();
  const isInRoute = hasShop(shop.id);
  const [showRoutePrompt, setShowRoutePrompt] = useState(false);

  // Use Context instead of local state/fetch
  const { getInteraction, toggleFavorite, toggleWishlist } = useShopInteractions();
  const interactions = getInteraction(shop.id);

  const isFavorite = interactions.favorite;
  const isInWishlist = interactions.wishlist;

  const [isToggling, setIsToggling] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);

  const handleToggleAction = async (e: React.MouseEvent, type: 'favorite' | 'wishlist') => {
    e.preventDefault();
    e.stopPropagation();

    if (!isLoggedIn) {
      setShowLoginModal(true);
      return;
    }

    if (isToggling) return;
    setIsToggling(true);

    try {
      if (type === 'favorite') {
        const newState = await toggleFavorite(shop.id);
        toast({
          title: newState ? t("routeDetail.addedFavorite") : t("routeDetail.removedFavorite"),
          duration: 2000,
        });
      } else {
        const newState = await toggleWishlist(shop.id);
        toast({
          title: newState ? t("routeDetail.addedBucketlist") : t("routeDetail.removedBucketlist"),
          duration: 2000,
        });
      }
    } catch (error) {
      toast({
        title: t("common.error"),
        description: t("report.errorDesc"),
        variant: "destructive",
      });
    } finally {
      setIsToggling(false);
    }
  };

  const handleReviewClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isLoggedIn) {
      setShowLoginModal(true);
      return;
    }
    setShowRatingModal(true);
  };

  const handleStartRoute = (action: 'add' | 'new') => {
    if (action === 'new') {
      clearRoute();
    }
    if (!hasShop(shop.id)) {
      addShop(shop);
    }
    setShowRoutePrompt(false);
    navigate("/route-builder");
  };

  // Calculate opening status
  const openingStatus = getOpeningStatus(
    shop.openingHoursStructured || [],
    openingStatusDay,
    {
      isClosed: !!shop.is_temporarily_closed,
      until: shop.temporarily_closed_until,
      reason: shop.temporarily_closed_reason
    },
    { locale, t }
  );

  return (
    <Card
      className={`hover-elevate overflow-hidden transition-all ${isSelected ? "border-primary border-2 bg-primary/5 shadow-md" : "border-border"
        }`}
      data-testid={`card-shop-${shop.id}`}
    >
      <CardContent className="p-0">
        <div className="flex items-stretch min-h-[128px]">
          {/* Shop Image - Vierkant, 30% breedte */}
          <div className="flex-shrink-0 relative overflow-hidden rounded-l-lg aspect-square w-[30%] self-start group">
            <ShopImage
              shopId={shop.id}
              profileImageUrl={shop.profileImageUrl}
              latestPostImageUrl={shop.latestPostImageUrl}
              fallbackImageUrl={shop.imageUrl}
              size="lg"
              className="h-full w-full object-cover"
            />

            {/* Route Index Badge - Top Left */}
            {routeIndex !== undefined && (
              <div className="absolute top-2 left-2 z-20 flex items-center justify-center min-w-[24px] h-6 px-1.5 rounded-full bg-primary text-primary-foreground text-xs font-bold shadow-md">
                {routeIndex}
              </div>
            )}

            {/* Goed Doel Badge - Alleen tonen als het een geautoriseerd goed doel is */}
            {shop.has_charity && (
              <div className={`absolute z-10 px-2 py-0.5 bg-black/70 backdrop-blur-sm rounded text-[10px] font-bold text-white uppercase tracking-wider ${routeIndex !== undefined ? 'top-2 right-2' : 'top-2 left-2'}`}>
                {t("shopCard.charity")}
              </div>
            )}

            {/* Horeca Badge */}
            {shop.has_horeca && (
              <div className={`absolute z-10 px-2 py-0.5 bg-primary/90 backdrop-blur-sm rounded text-[10px] font-bold text-white uppercase tracking-wider ${shop.has_charity ? (routeIndex !== undefined ? 'top-8 right-2' : 'top-8 left-2') : (routeIndex !== undefined ? 'top-2 right-2' : 'top-2 left-2')}`}>
                {t("addShop.horeca")}
              </div>
            )}

            {/* Overlay buttons removed - only using buttons at bottom right now */}
          </div>

          {/* Content - Flex layout met info en knoppen */}
          <div className="flex-1 flex flex-col p-4 min-w-0 justify-between">
            {/* Top section: Naam, badges, adres, ratings */}
            <div className="space-y-2">
              <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                <div className="flex items-center gap-1.5">
                  <h3 className="font-bold text-base leading-tight break-words max-w-full" data-testid={`text-shop-name-${shop.id}`}>
                    {typeof shop.name === 'string' ? shop.name : (shop.name as any)?.value || "Naamloos"}
                  </h3>
                  {shop.isVerified && (
                    <CheckCircle className="h-4 w-4 text-primary flex-shrink-0" data-testid={`badge-verified-${shop.id}`} />
                  )}
                  {shop.parking_rating_avg && (
                    <div className="flex text-primary gap-0.5" title={`${shop.parking_rating_avg}/3 Parkeren`}>
                      {Array.from({ length: Math.round(shop.parking_rating_avg) }).map((_, i) => (
                        <Car key={i} className="h-4 w-4 fill-primary" />
                      ))}
                    </div>
                  )}
                </div>
                {shop.price_level_avg && (
                  <PriceLevel
                    level={shop.price_level_avg}
                    country={shop.country || "NL"}
                    size="sm"
                    className="sm:ml-1"
                  />
                )}
              </div>

              {/* Category Icons - Moved under title */}
              {shop.categories && Array.isArray(shop.categories) && shop.categories.length > 0 && (
                <div className="flex items-center gap-2 mt-1 mb-1 flex-wrap">
                  {shop.categories.map((cat: string) => (
                    <div
                      key={cat}
                      className="flex items-center justify-center text-foreground transition-colors hover:text-foreground/80 cursor-help"
                      title={cat}
                    >
                      <CategoryIcon category={cat} size={16} />
                    </div>
                  ))}
                </div>
              )}

              {/* Volledig adres */}
              <div className="flex flex-col gap-1.5">
                <div className="flex items-start gap-2 text-sm text-muted-foreground">
                  <MapPin className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    {shop.address && (
                      <div className="font-medium text-foreground">{typeof shop.address === 'string' ? shop.address : (shop.address as any)?.value || ""}</div>
                    )}
                    <div>
                      {typeof shop.city === 'string' ? shop.city : (shop.city as any)?.value || "Onbekend"}{shop.province ? `, ${shop.province}` : ""}
                    </div>
                  </div>
                </div>

                {/* Opening Status */}
                <div className="flex items-center gap-2 text-sm">
                  <ClockIcon className={`h-4 w-4 flex-shrink-0 ${openingStatus.isOpen ? 'text-green-500' : 'text-muted-foreground'}`} />
                  <span className={openingStatus.colorClass}>
                    {openingStatus.status}
                  </span>
                </div>
              </div>

              {/* Badges */}
              <div className="flex items-center gap-2 flex-wrap">
                {routeCount > 0 && (
                  <Badge variant="secondary" className="text-xs">
                    <Route className="h-3 w-3 mr-1" />
                    {t("shopCard.inRoutes", { count: routeCount })}
                  </Badge>
                )}
                {shop.isVerified && (
                  <Badge variant="outline" className="text-xs">
                    {t("shopCard.verified")}
                  </Badge>
                )}

                {/* Payment methods section - Explicit separate section */}
                {(shop.has_cash_payment || shop.has_card_payment) && (
                  <div className="mt-2 pt-2 border-t border-border">
                    <p className="text-xs font-semibold text-muted-foreground mb-1">{t("shopCard.paymentOptions")}</p>
                    <div className="flex gap-2">
                      {shop.has_cash_payment && (
                        <Badge variant="outline" className="text-[10px] h-5 px-1.5 bg-green-50 text-green-700 border-green-200">
                          {t("shopCard.cash")}
                        </Badge>
                      )}
                      {shop.has_card_payment && (
                        <Badge variant="outline" className="text-[10px] h-5 px-1.5 bg-blue-50 text-blue-700 border-blue-200">
                          {t("shopCard.card")}
                        </Badge>
                      )}
                    </div>
                  </div>
                )}

                {/* Opening hours - REMOVED from card to keep it clean, only visible on detail page */}


                {/* DRIE RATINGS: VintMap Community, Eigen, Google */}
                <div className="pt-2 border-t border-border">
                  <ShopRating
                    communityRating={communityRating}
                    communityReviewCount={communityReviewCount}
                    ownerRating={ownerRating}
                    googleRating={googleRating}
                    googleReviewCount={googleReviewCount}
                    googleReviewsUrl={googleReviewsUrl}
                    showLabels={true}
                    shopId={shop.id}
                    shopName={typeof shop.name === 'string' ? shop.name : (shop.name as any)?.value || "Winkel"}
                    fetchUserReview={false}
                  />
                </div>
              </div>
            </div>

            {/* Bottom section: Knoppen rechtsonder */}
            <div className="flex items-center justify-end gap-2 pt-2 border-t border-border mt-auto">
              {/* Category Icons REMOVED from here */}

              <div className="flex items-center gap-1">
                <Button
                  size="sm"
                  variant="ghost"
                  className={`h-8 px-2 flex items-center gap-1 rounded-full ${isFavorite ? 'text-primary hover:text-primary/80' : 'text-muted-foreground'}`}
                  onClick={(e) => handleToggleAction(e, 'favorite')}
                  title={isFavorite ? t("shopCard.removeFavorite") : t("shopCard.addFavorite")}
                >
                  <Heart className={`h-4 w-4 ${isFavorite ? 'fill-current' : ''}`} />
                  {shop.likesCount !== undefined && shop.likesCount > 0 && (
                    <span className="text-xs font-medium">{shop.likesCount}</span>
                  )}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className={`h-8 px-2 flex items-center gap-1 rounded-full ${isInWishlist ? 'text-primary hover:text-primary/80' : 'text-muted-foreground'}`}
                  onClick={(e) => handleToggleAction(e, 'wishlist')}
                  title={isInWishlist ? t("shopCard.removeWishlist") : t("shopCard.addWishlist")}
                >
                  <Bookmark className={`h-4 w-4 ${isInWishlist ? 'fill-current' : ''}`} />
                  {shop.savesCount !== undefined && shop.savesCount > 0 && (
                    <span className="text-xs font-medium">{shop.savesCount}</span>
                  )}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-8 w-8 p-0 flex items-center justify-center rounded-full text-muted-foreground hover:text-primary transition-colors"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setShareModalOpen(true);
                  }}
                  title={t("share.titlePrefix")}
                >
                  <Share2 className="h-4 w-4" />
                </Button>
              </div>

              {/* Action Buttons - Right side */}
              <div className="flex items-center gap-2">



                {/* Google Maps Navigation Button - Available for all users */}
                <Button
                  variant="outline"
                  size="sm"
                  className="h-9 px-3 border-slate-200 text-slate-700 hover:bg-slate-50"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    logAnalyticsEvent(shop.id, 'nav_profile');
                    const url = getShopNavigationUrl(shop);
                    openGoogleMaps(url);
                  }}
                  title={t("shopCard.navigateToShop")}
                >
                  <Navigation className="h-4 w-4 mr-1.5" />
                  <span className="hidden sm:inline">{t("shopCard.maps")}</span>
                </Button>

                {/* Route Builder Button - Always visible for Pro users */}
                <Button
                  variant="outline"
                  size="sm"
                  className={`h-9 px-3 font-medium ${isInRoute
                    ? 'bg-primary/10 hover:bg-primary/15 border-primary text-primary'
                    : 'bg-primary/5 hover:bg-primary/10 border-primary/20 text-primary'
                    }`}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();

                    if (!isLoggedIn) {
                      setShowLoginModal(true);
                      return;
                    }

                    if (!isInsider) {
                      setShowUpgradeModal(true);
                      return;
                    }

                    // Route planning is exclusively for Pro users
                    if (currentRouteShops.length > 0 && !isInRoute) {
                      setShowRoutePrompt(true);
                    } else {
                      handleStartRoute('add');
                    }
                  }}
                  data-testid={`button-add-shop-${shop.id}`}
                >
                  {isInRoute ? t("shopCard.inRoute") : t("shopCard.planRoute")}
                </Button>


                {/* View Shop Button */}
                {(() => {
                  const url = getShopProfileUrl({
                    id: shop.id,
                    slug: shop.slug,
                    province: shop.province ? String(shop.province) : null,
                    country: shop.country,
                    city: typeof shop.city === 'string' ? shop.city : (shop.city as any)?.value,
                    name: typeof shop.name === 'string' ? shop.name : (shop.name as any)?.value
                  }, locale);
                  return (
                    <Link href={url}>
                      <Button size="sm" className="h-9" data-testid={`button-view-shop-${shop.id}`}>
                        {t("common.view")}
                      </Button>
                    </Link>
                  );
                })()}
              </div>
            </div>
          </div>
        </div>
      </CardContent>
      <LoginModal open={showLoginModal} onOpenChange={setShowLoginModal} />
      <UpgradeModal open={showUpgradeModal} onOpenChange={setShowUpgradeModal} variant="route-builder" />

      <Dialog open={showRoutePrompt} onOpenChange={setShowRoutePrompt}>
        <DialogContent className="sm:max-w-md" onClick={(e) => e.stopPropagation()}>
          <DialogHeader>
            <DialogTitle>{t("routeBuilder.composeRoute")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <p className="text-muted-foreground">
              {t("shopDetail.addToExistingOrNewRoute")}
            </p>
            <div className="flex flex-col gap-3 pt-2">
              <Button onClick={(e) => { e.stopPropagation(); handleStartRoute('add'); }} className="w-full">
                <Package className="h-4 w-4 mr-2" />
                {t("shopDetail.addToExistingRoute")}
              </Button>
              <Button variant="outline" onClick={(e) => { e.stopPropagation(); handleStartRoute('new'); }} className="w-full">
                <Route className="h-4 w-4 mr-2" />
                {t("shopDetail.startNewRoute")}
              </Button>
              <Button variant="ghost" onClick={(e) => { e.stopPropagation(); setShowRoutePrompt(false); }} className="w-full">
                {t("shops.cancel")}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      <RatingModal
        open={showRatingModal}
        onOpenChange={setShowRatingModal}
        shopId={shop.id}
        shopName={typeof shop.name === 'string' ? shop.name : (shop.name as any)?.value || "Winkel"}
        existingReview={null} // We don't fetch the existing review here for simplicity in listing, assumes new or overwrite
        onSuccess={() => {
          toast({
            title: t("rating.saveSuccess"),
            description: t("rating.saveSuccessDesc"),
          });
          // Optionally invalidate queries if needed, but the list might not need immediate refresh of the rating number
        }}
      />
      <ShareModal
        open={shareModalOpen}
        onOpenChange={setShareModalOpen}
        title={(function () {
          const nameVal = shop?.name;
          return typeof nameVal === 'string' ? nameVal : (nameVal as any)?.value || "Kringloopwinkel";
        })()}
        text={(function () {
          const name = typeof shop?.name === 'string' ? shop.name : (shop?.name as any)?.value || "Kringloopwinkel";
          const address = typeof shop?.address === 'string' ? shop.address : (shop?.address as any)?.value || "";
          const city = typeof shop?.city === 'string' ? shop.city : (shop?.city as any)?.value || "";
          const rating = shop?.rating?.toFixed(1) || "-";
          const reviews = shop?.vintMapReviewCount || 0;

          return `${t("shopDetail.sharePrefix")} ${name} ${t("shopDetail.shareSuffix")}\\n\\n📍 ${address}, ${city}\\n⭐ ${rating} (${reviews} ${t("shopDetail.reviewsLabel")})`;
        })()}
        url={(function () {
          const baseUrl = window.location.origin;
          const url = getShopProfileUrl({
            id: shop.id,
            slug: shop.slug,
            province: shop.province ? String(shop.province) : null,
            country: shop.country,
            city: typeof shop.city === 'string' ? shop.city : (shop.city as any)?.value,
            name: typeof shop.name === 'string' ? shop.name : (shop.name as any)?.value
          }, locale);
          return `${baseUrl}${url}`;
        })()}
        imageUrl={(shop as any)?.share_image_url || undefined}
        entityType="shop"
        entityId={shop?.id}
      />
    </Card >
  );
}
